import { useCallback, useEffect, useRef } from 'react';
import { useAuthContext } from '../../auth/useAuthContext';
import { useSupportBoardMutation } from './useSupportBoardMutation';
import { useSupportBoardState } from './useSupportBoardState';

declare let SBF: any;
declare let SBChat: any;

declare let $: any;

type CloseButtonType = Pick<
  CSSStyleDeclaration,
  | 'position'
  | 'zIndex'
  | 'fontFamily'
  | 'fontSize'
  | 'width'
  | 'height'
  | 'right'
  | 'top'
  | 'cursor'
  | 'display'
  | 'justifyContent'
  | 'alignItems'
  | 'color'
  | 'borderRadius'
>;

const XIcon =
  '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" aria-hidden="true" role="img" class="MuiBox-root css-1t9pz9x iconify iconify--tabler" width="1em" height="1em" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 6L6 18M6 6l12 12"></path></svg>';

export const useSupportBoardHooks = () => {
  const { user } = useAuthContext();
  const spanObserver = useRef<MutationObserver>();

  const {
    loggedIn,
    supportDiv,
    counterSpan,
    setSupportLoaded,
    setLoggedIn,
    setSupportDiv,
    setCounterSpan,
    setCounter,
  } = useSupportBoardState();

  const { getToken } = useSupportBoardMutation();

  const sendCounter = useCallback(() => {
    setCounter(counterSpan?.innerText!);
  }, [counterSpan, setCounter]);

  const login = useCallback(
    async ({ userId, token }: { userId: number; token: string }) => {
      await new Promise((resolve) =>
        SBF.login(null, null, userId, token, () => {
          SBChat.initChat(resolve);
        }),
      );
      setLoggedIn(true);
    },
    [setLoggedIn],
  );

  const createUserAndLogin = useCallback(async () => {
    const { helpCenterUserId, helpCenterToken } = await getToken();
    await login({ userId: helpCenterUserId, token: helpCenterToken });
  }, [getToken, login]);

  const openSupport = useCallback(async () => {
    if (!loggedIn) {
      await createUserAndLogin();
    }
    if (!supportDiv) return;
    supportDiv.setAttribute(
      'style',
      'display: block; bottom: 20px; right: 60px;',
    );
    supportDiv.classList.add('sb-active');
  }, [loggedIn, createUserAndLogin, supportDiv]);

  const setButton = (div: HTMLDivElement) => {
    const sbBtn = div?.querySelector('.sb-chat-btn');
    if (sbBtn) {
      sbBtn.setAttribute('style', 'display: none');
      const counter = sbBtn.getElementsByTagName('span')[0];
      setCounterSpan(counter);
      spanObserver?.current?.observe(counter, {
        subtree: true,
        childList: true,
      });
    }
  };

  const createCloseButton = (div: HTMLDivElement) => {
    const closeButtonStyles: CloseButtonType = {
      position: 'absolute',
      zIndex: '100',
      fontFamily: 'Nunito',
      fontSize: '12px',
      width: '25px',
      height: '25px',
      right: '10px',
      top: '10px',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#FFF',
      borderRadius: '50%',
    };

    const oldCloseButton = document.querySelector('.sb-icon-close');
    if (oldCloseButton) {
      oldCloseButton.setAttribute('style', 'display: none');
    }

    const closeButton = document.createElement('div');
    Object.entries(closeButtonStyles).map(
      ([key, value]) => (closeButton.style[key as any] = value),
    );
    closeButton.addEventListener('click', () => {
      div.classList.remove('sb-active');
    });
    closeButton.innerHTML = XIcon;
    const sbBody = document.querySelector('.sb-body');
    sbBody?.append(closeButton);
  };

  const handleSupportDiv = async (div: HTMLDivElement) => {
    setSupportDiv(div);
    setButton(div);
    createCloseButton(div);
  };

  const loadSupport = () => {
    const scriptElement = document.createElement('script');
    scriptElement.src = 'https://support.copera.ai/js/main.js';
    scriptElement.id = 'sbinit';
    document.getElementsByTagName('head')[0].appendChild(scriptElement);

    $(document).on('SBReady', async () => {
      const supportDiv: HTMLDivElement | null =
        document.querySelector('.sb-main');

      if (supportDiv) {
        setSupportLoaded(true);
        await handleSupportDiv(supportDiv);
        if (user?.sbUserId && user?.sbToken) {
          await login({ userId: user?.sbUserId, token: user?.sbToken });
        }
      }
    });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    const callback = () => {
      sendCounter();
    };
    spanObserver.current = new MutationObserver(callback);

    return () => spanObserver?.current?.disconnect();
  }, []);

  return {
    loadSupport,
    openSupport,
  };
};
